import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';

import {
  useDismissPharmacyWarning,
  useResolvePharmacyWarning,
} from '@/lib/api/adminHooks';
import type {
  PharmacyWarningActionType,
  PharmacyWarningRow,
} from '@/types/admin';

import { PatientWarningsModal } from './PatientWarningsModal';

/**
 * Shared full-detail card used in the dedicated warnings screen. Renders
 * supplemental notes, current order/patient/shipping context, and the
 * action panel (when unresolved) or resolution metadata (when resolved).
 */
export function PharmacyWarningCard({ w }: { w: PharmacyWarningRow }) {
  const code = w.reason_code ?? 0;
  const recommendedAction = recommendedActionForCode(code);
  const [openAction, setOpenAction] = useState<PharmacyWarningActionType | null>(
    null,
  );
  const [patientModalOpen, setPatientModalOpen] = useState(false);

  const isResolved = Boolean(w.resolved_at);

  const receivedLabel = w.received_at
    ? format(parseISO(w.received_at), 'MMM d, h:mm a')
    : '—';

  const patientLabel =
    [w.patient_first_name, w.patient_last_name].filter(Boolean).join(' ') ||
    w.patient_email ||
    w.patient_id ||
    '—';

  return (
    <div
      className={
        isResolved
          ? ' border border-subtle bg-surface-soft/40 p-16 flex flex-col gap-12'
          : ' border border-default bg-surface-card p-16 flex flex-col gap-12'
      }
    >
      <div className="flex items-start justify-between gap-12">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-8 flex-wrap">
            <span className="type-label-sm text-ink-emphasis bg-surface-soft border border-default rounded-sm px-6 py-2">
              {code} · {w.reason_description || 'no description'}
            </span>
            <span className="type-body-xs text-ink-muted">
              {w.partner_key} · received {receivedLabel}
            </span>
            {isResolved ? (
              <span className="type-label-xs text-status-success bg-status-success/10 border border-status-success/30 rounded-sm px-6 py-2">
                resolved
              </span>
            ) : null}
          </div>
          <p className="type-body-sm text-ink">
            <span className="text-ink-muted">Order:</span>{' '}
            <code className="type-mono-sm">{w.order_number || w.order_id}</code>
            {w.product_label ? (
              <span className="text-ink-muted"> · {w.product_label}</span>
            ) : null}
          </p>
          <p className="type-body-sm text-ink">
            <span className="text-ink-muted">Patient:</span>{' '}
            {w.patient_id ? (
              <button
                type="button"
                onClick={() => setPatientModalOpen(true)}
                className="text-accent hover:underline"
              >
                {patientLabel}
              </button>
            ) : (
              patientLabel
            )}
          </p>
          {w.current_carrier || w.current_shipping_method ? (
            <p className="type-body-xs text-ink-muted">
              Current ship: {w.current_carrier || '—'} ·{' '}
              {w.current_shipping_method || '—'}
            </p>
          ) : null}
          {w.current_address?.line1 ? (
            <p className="type-body-xs text-ink-muted">
              Current address: {w.current_address.line1}
              {w.current_address.line2 ? `, ${w.current_address.line2}` : ''},{' '}
              {w.current_address.city}, {w.current_address.state}{' '}
              {w.current_address.postal_code}
            </p>
          ) : null}
        </div>
      </div>

      {w.notes ? (
        <div className="rounded-sm bg-surface-soft p-12 border border-subtle">
          <p className="type-label-xs text-ink-muted mb-4">
            Supplemental notes from pharmacy
          </p>
          <p className="type-body-sm text-ink whitespace-pre-wrap">{w.notes}</p>
        </div>
      ) : (
        <p className="type-body-xs text-ink-muted italic">No supplemental notes.</p>
      )}

      {isResolved ? (
        <ResolutionMetadata w={w} />
      ) : (
        <ActionPanel
          w={w}
          recommendedAction={recommendedAction}
          openAction={openAction}
          setOpenAction={setOpenAction}
        />
      )}

      {patientModalOpen ? (
        <PatientWarningsModal
          seed={w}
          onClose={() => setPatientModalOpen(false)}
        />
      ) : null}
    </div>
  );
}

function ResolutionMetadata({ w }: { w: PharmacyWarningRow }) {
  const at = w.resolved_at ? format(parseISO(w.resolved_at), 'MMM d, h:mm a') : '—';
  return (
    <div className="rounded-sm border border-subtle p-10 flex flex-col gap-2">
      <p className="type-label-xs text-ink-muted">Resolution</p>
      <p className="type-body-sm text-ink">
        <span className="text-ink-muted">Resolved:</span> {at}{' '}
        <span className="text-ink-muted">via</span>{' '}
        <code className="type-mono-sm">{w.resolved_via || '—'}</code>
      </p>
      {w.resolved_by ? (
        <p className="type-body-xs text-ink-muted">
          By: <code className="type-mono-sm">{w.resolved_by}</code>
        </p>
      ) : null}
      {w.resolved_note ? (
        <p className="type-body-sm text-ink">
          <span className="text-ink-muted">Note:</span> {w.resolved_note}
        </p>
      ) : null}
    </div>
  );
}

function ActionPanel({
  w,
  recommendedAction,
  openAction,
  setOpenAction,
}: {
  w: PharmacyWarningRow;
  recommendedAction: PharmacyWarningActionType | null;
  openAction: PharmacyWarningActionType | null;
  setOpenAction: (a: PharmacyWarningActionType | null) => void;
}) {
  return (
    <>
      <div className="flex items-center gap-8 flex-wrap">
        {(['update_fill_address', 'update_fill_method', 'update_patient', 'cancel_fill'] as PharmacyWarningActionType[]).map((t) => (
          <button
            key={t}
            type="button"
            className={
              openAction === t
                ? 'btn-ghost btn-sm'
                : t === recommendedAction
                  ? 'btn-primary btn-sm'
                  : 'btn-secondary btn-sm'
            }
            onClick={() =>
              setOpenAction(openAction === t ? null : t)
            }
          >
            {openAction === t ? 'Close' : labelForAction(t)}
            {t === recommendedAction && openAction !== t ? ' ★' : ''}
          </button>
        ))}
        <DismissButton w={w} />
      </div>

      {openAction ? (
        <ActionForm
          w={w}
          actionType={openAction}
          onDone={() => setOpenAction(null)}
        />
      ) : null}
    </>
  );
}

export function recommendedActionForCode(
  code: number,
): PharmacyWarningActionType | null {
  if (code === 1040 || code === 2010) return 'update_fill_address';
  if (code === 1060 || code === 1070) return 'update_patient';
  if (code === 1130) return 'update_fill_method';
  if (code === 2020 || code === 3020) return 'cancel_fill';
  return null;
}

export function labelForAction(t: PharmacyWarningActionType): string {
  switch (t) {
    case 'update_fill_address':
      return 'Update shipping address';
    case 'update_fill_method':
      return 'Update shipping method';
    case 'update_patient':
      return 'Update patient info';
    case 'cancel_fill':
      return 'Cancel fill';
  }
}

function DismissButton({ w }: { w: PharmacyWarningRow }) {
  const dismiss = useDismissPharmacyWarning();
  return (
    <button
      type="button"
      className="btn-ghost btn-sm"
      disabled={dismiss.isPending}
      onClick={() => {
        if (w.reason_code == null) return;
        const note = window.prompt('Optional reason for dismissing:') || '';
        dismiss.mutate({
          orderId: w.order_id,
          body: { reason_code: w.reason_code, note: note || undefined },
        });
      }}
    >
      {dismiss.isPending ? 'Dismissing…' : 'Dismiss'}
    </button>
  );
}

const CARRIER_OPTIONS = ['usps', 'ups'];
const METHOD_OPTIONS_BY_CARRIER: Record<string, string[]> = {
  usps: ['usps_first', 'usps_ground_advantage'],
  ups: [
    'ups_next_day_air',
    'ups_second_day_air',
    'ups_second_day_air_asr',
    'ups_next_day_air_saver_asr',
  ],
};

function ActionForm({
  w,
  actionType,
  onDone,
}: {
  w: PharmacyWarningRow;
  actionType: PharmacyWarningActionType;
  onDone: () => void;
}) {
  const resolve = useResolvePharmacyWarning();
  const initialValues = useMemo(() => initialFormValues(w, actionType), [w, actionType]);
  const [form, setForm] = useState<Record<string, string>>(initialValues);

  const update = (k: string, v: string) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const onSubmit = () => {
    if (w.reason_code == null) return;
    const params = pickParamsForAction(actionType, form);
    resolve.mutate(
      {
        orderId: w.order_id,
        body: { reason_code: w.reason_code, action: { type: actionType, params } },
      },
      { onSuccess: onDone },
    );
  };

  return (
    <div className="rounded-sm border border-subtle bg-surface-soft p-12 flex flex-col gap-8">
      {actionType === 'update_fill_address' ? (
        <AddressFields form={form} update={update} />
      ) : null}
      {actionType === 'update_fill_method' ? (
        <MethodFields form={form} update={update} />
      ) : null}
      {actionType === 'update_patient' ? (
        <PatientFields form={form} update={update} />
      ) : null}
      {actionType === 'cancel_fill' ? (
        <p className="type-body-sm text-ink-muted">
          This will permanently cancel fill <code>{w.partner_order_id}</code> at{' '}
          {w.partner_key}.
        </p>
      ) : null}

      {resolve.isError ? (
        <p className="type-body-xs text-status-danger">
          Failed: {String((resolve.error as Error)?.message || resolve.error)}
        </p>
      ) : null}

      <div className="flex items-center gap-8">
        <button
          type="button"
          className="btn-primary btn-sm"
          disabled={resolve.isPending}
          onClick={onSubmit}
        >
          {resolve.isPending ? 'Submitting…' : 'Submit'}
        </button>
        <button type="button" className="btn-ghost btn-sm" onClick={onDone}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function initialFormValues(
  w: PharmacyWarningRow,
  t: PharmacyWarningActionType,
): Record<string, string> {
  if (t === 'update_fill_address') {
    return {
      line1: w.current_address?.line1 ?? '',
      line2: w.current_address?.line2 ?? '',
      city: w.current_address?.city ?? '',
      state: w.current_address?.state ?? '',
      postal_code: w.current_address?.postal_code ?? '',
    };
  }
  if (t === 'update_fill_method') {
    return {
      carrier: w.current_carrier ?? 'usps',
      shipping_method: w.current_shipping_method ?? 'usps_ground_advantage',
    };
  }
  if (t === 'update_patient') {
    return {
      first_name: w.patient_first_name ?? '',
      last_name: w.patient_last_name ?? '',
      birth_date: '',
      phone_number: '',
      email_address: w.patient_email ?? '',
    };
  }
  return {};
}

function pickParamsForAction(
  t: PharmacyWarningActionType,
  form: Record<string, string>,
): Record<string, string> {
  if (t === 'cancel_fill') return {};
  if (t === 'update_fill_address') {
    return {
      line1: form.line1 || '',
      line2: form.line2 || '',
      city: form.city || '',
      state: form.state || '',
      postal_code: form.postal_code || '',
    };
  }
  if (t === 'update_fill_method') {
    return {
      carrier: form.carrier || '',
      shipping_method: form.shipping_method || '',
    };
  }
  const out: Record<string, string> = {
    first_name: form.first_name || '',
    last_name: form.last_name || '',
  };
  if (form.birth_date) out.birth_date = form.birth_date;
  if (form.phone_number) out.phone_number = form.phone_number;
  if (form.email_address) out.email_address = form.email_address;
  return out;
}

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-2">
      <span className="type-label-xs text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'border border-default rounded-sm px-8 py-4 type-body-sm bg-surface-card text-ink';

function AddressFields({
  form,
  update,
}: {
  form: Record<string, string>;
  update: (k: string, v: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-8">
      <FieldRow label="Line 1">
        <input
          className={inputCls}
          value={form.line1}
          onChange={(e) => update('line1', e.target.value)}
        />
      </FieldRow>
      <FieldRow label="Line 2">
        <input
          className={inputCls}
          value={form.line2}
          onChange={(e) => update('line2', e.target.value)}
        />
      </FieldRow>
      <FieldRow label="City">
        <input
          className={inputCls}
          value={form.city}
          onChange={(e) => update('city', e.target.value)}
        />
      </FieldRow>
      <FieldRow label="State (2 char)">
        <input
          className={inputCls}
          maxLength={2}
          value={form.state}
          onChange={(e) => update('state', e.target.value.toUpperCase())}
        />
      </FieldRow>
      <FieldRow label="Postal code">
        <input
          className={inputCls}
          value={form.postal_code}
          onChange={(e) => update('postal_code', e.target.value)}
        />
      </FieldRow>
    </div>
  );
}

function MethodFields({
  form,
  update,
}: {
  form: Record<string, string>;
  update: (k: string, v: string) => void;
}) {
  const methodOpts = METHOD_OPTIONS_BY_CARRIER[form.carrier] || [];
  return (
    <div className="grid grid-cols-2 gap-8">
      <FieldRow label="Carrier">
        <select
          className={inputCls}
          value={form.carrier}
          onChange={(e) => {
            const carrier = e.target.value;
            const nextMethod = METHOD_OPTIONS_BY_CARRIER[carrier]?.[0] || '';
            update('carrier', carrier);
            update('shipping_method', nextMethod);
          }}
        >
          {CARRIER_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </FieldRow>
      <FieldRow label="Shipping method">
        <select
          className={inputCls}
          value={form.shipping_method}
          onChange={(e) => update('shipping_method', e.target.value)}
        >
          {methodOpts.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </FieldRow>
    </div>
  );
}

function PatientFields({
  form,
  update,
}: {
  form: Record<string, string>;
  update: (k: string, v: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-8">
      <FieldRow label="First name">
        <input
          className={inputCls}
          value={form.first_name}
          onChange={(e) => update('first_name', e.target.value)}
        />
      </FieldRow>
      <FieldRow label="Last name">
        <input
          className={inputCls}
          value={form.last_name}
          onChange={(e) => update('last_name', e.target.value)}
        />
      </FieldRow>
      <FieldRow label="DOB (YYYY-MM-DD)">
        <input
          className={inputCls}
          placeholder="1991-02-03"
          value={form.birth_date}
          onChange={(e) => update('birth_date', e.target.value)}
        />
      </FieldRow>
      <FieldRow label="Phone (E.164)">
        <input
          className={inputCls}
          placeholder="+15551234567"
          value={form.phone_number}
          onChange={(e) => update('phone_number', e.target.value)}
        />
      </FieldRow>
      <FieldRow label="Email">
        <input
          className={inputCls}
          value={form.email_address}
          onChange={(e) => update('email_address', e.target.value)}
        />
      </FieldRow>
    </div>
  );
}
